import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation, useParams, useSearchParams } from 'react-router-dom'

import { Box, Link, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import request from 'api'
import { Loading } from 'components/ui'
import { getGlobalConsentApi } from 'pages/policies/api'
import { Consent, LocationState, buildPolicySearch } from 'pages/policies/utils'
import Detail from 'pages/policies/views/detail'

import { useDateTime, useMappedTranslation } from '../../../hooks'

export default function TermsConditionsService() {
  const { appId } = useParams()
  const { state }: { state: LocationState | undefined } = useLocation()
  const [searchParams] = useSearchParams()
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const policySearch = buildPolicySearch(searchParams)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [title, setTitle] = useState<string>('')
  const [detail, setDetail] = useState<string>('')
  const t = useMappedTranslation()
  const theme = useTheme()
  const dateTime = useDateTime()

  const getConsent = () => {
    if (state?.consent !== undefined) {
      return Promise.resolve(state.consent)
    }

    return getGlobalConsentApi('TC', true, { overrides: { countryCode: paramCountryCode, locale: paramLocale } }).then((res) => {
      if (res.status !== 200) {
        throw new Error('Failed getConsentApi')
      } else {
        const data = res.data as Consent[]
        return data.find((item) => item.appKey === appId)
      }
    })
  }

  const renderAction = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          pt: { xs: '52px', md: '72px' }
        }}
      >
        <Typography
          className='off'
          fontSize='13px'
          lineHeight='16px'
          fontWeight='700'
          color={theme.palette.mode === 'dark' ? '#999999' : '#636363'}
        >
          {t('iam.01.Common.Txt.Effective.date', { 0: dateTime(Number(state?.consent?.publish), { format: 'withoutTime' }) })}
        </Typography>
        <Link
          to={`/policies/terms-conditions/services/${appId}${policySearch}`}
          component={RouterLink}
          sx={{
            mt: '12px',
            padding: '7px 12px',
            fontSize: '13px',
            lineHeight: '19px',
            minHeight: '0',
            textTransform: 'none',
            color: 'text.primary',
            textDecoration: 'none',
            border: `0.5px solid #636363`,
            borderRadius: '24px',
            textAlign: 'center',
            minWidth: '152px',
            display: 'block',
            height: '33px'
          }}
          data-testid='LinkE6467EF0'
        >
          {t('iam.01.Common.Notice.detail.list.01')}
        </Link>
      </Box>
    )
  }

  useEffect(() => {
    getConsent().then((data) => {
      if (data?.uri === undefined) {
        throw new Error('Failed getConsentApi')
      }

      setTitle(data.title)

      return request
        .get(data.uri)
        .then((res) => {
          if (res.status !== 200) {
            throw new Error('Failed getConsentApi')
          }
          const dom = new DOMParser().parseFromString(res.data, 'text/html')
          const body = dom.getElementsByTagName('body').item(0)?.innerHTML
          if (body !== undefined) {
            setDetail(body)
          }
        })
        .catch(() => {
          setDetail('<div>Failed to load</div>')
        })
        .finally(() => {
          setIsLoading(false)
        })
    })
  }, [paramCountryCode, paramLocale])

  return (
    <Box sx={{ width: '100%', height: '100%', padding: { xs: '24px', md: '0' }, paddingTop: { xs: '5px', md: '46px' } }}>
      {isLoading ? (
        <Loading fullScreen />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: { xs: '100%', md: '1040px' } }}>
            <Detail title={title || ''} isLoading={isLoading} detail={detail} action={renderAction()} />
          </Box>
        </Box>
      )}
    </Box>
  )
}
